import React from 'react';
import { motion } from 'framer-motion';
import { Bed, Users, Maximize, Wifi, Coffee, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Accommodation() {
  const rooms = [{
    id: 'godavari-suite',
    name: 'The Godavari Suite',
    image: 'https://images.unsplash.com/photo-1590490360182-c33d57733427?ixlib=rb-4.0.3&auto=format&fit=crop&w=1074&q=80',
    desc: 'Our most prestigious accommodation, featuring a separate living area, antique furnishings, and panoramic views of the club gardens.',
    details: {
      size: '85 sq m',
      occupancy: 'Up to 3 guests',
      bed: '1 King Bed'
    },
    amenities: ['Complimentary Breakfast', 'Evening Turndown', 'Personal Butler Service']
  }, {
    id: 'club-room',
    name: 'Club Double Room',
    image: 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
    desc: 'Elegantly appointed rooms offering comfort and tranquility, perfect for short stays or visiting guests of members.',
    details: {
      size: '45 sq m',
      occupancy: 'Up to 2 guests',
      bed: '1 Queen Bed or 2 Twin Beds'
    },
    amenities: ['En-suite Bathroom', 'Work Desk', 'Nespresso Machine']
  }];
  return <div className="w-full bg-club-canvas" data-id="element-334">
      {/* Page Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden" data-id="element-335">
        <div className="absolute inset-0 z-0" data-id="element-336">
          <img src="https://images.unsplash.com/photo-1542314831-c6a4d14d8c53?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Luxury Accommodation" className="w-full h-full object-cover" data-id="element-337" />
          <div className="absolute inset-0 bg-club-navy/40 mix-blend-multiply" data-id="element-338"></div>
        </div>
        <div className="relative z-10 text-center px-4 mt-16" data-id="element-339">
          <motion.span className="eyebrow text-club-gold mb-4 block" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-340">
            Residential Rooms
          </motion.span>
          <motion.h1 className="text-5xl md:text-6xl font-serif text-white mb-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.6
        }} data-id="element-341">
            Accommodation
          </motion.h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 px-4 max-w-4xl mx-auto text-center" data-id="element-342">
        <p className="text-xl text-club-ink/80 font-light leading-relaxed mb-8" data-id="element-343">
          Andhra Social & Cultural Association offers twenty-four exquisitely
          appointed rooms and suites, providing a luxurious home away from home
          for our members and their guests. Each room blends classic elegance
          with modern comforts.
        </p>
      </section>

      {/* Rooms List */}
      <section className="py-12 px-4 max-w-7xl mx-auto mb-24" data-id="element-344">
        <div className="space-y-16" data-id="element-345">
          {rooms.map((room, index) => <motion.div key={room.id} className="flex flex-col lg:flex-row gap-12 bg-white border border-club-border rounded-sm overflow-hidden" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8
        }} data-id="element-346">
              <div className="w-full lg:w-1/2" data-id="element-347">
                <div className="h-full min-h-[300px] relative" data-id="element-348">
                  <img src={room.image} alt={room.name} className="absolute inset-0 w-full h-full object-cover" data-id="element-349" />
                </div>
              </div>
              <div className="w-full lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center" data-id="element-350">
                <h2 className="text-3xl mb-4" data-id="element-351">{room.name}</h2>
                <p className="text-club-ink/70 leading-relaxed mb-8" data-id="element-352">
                  {room.desc}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-353">
                  <div className="flex items-center gap-2 text-sm text-club-ink/80" data-id="element-354">
                    <Maximize size={16} className="text-club-gold" data-id="element-355" />
                    <span data-id="element-356">{room.details.size}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-club-ink/80" data-id="element-357">
                    <Users size={16} className="text-club-gold" data-id="element-358" />
                    <span data-id="element-359">{room.details.occupancy}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-club-ink/80" data-id="element-360">
                    <Bed size={16} className="text-club-gold" data-id="element-361" />
                    <span data-id="element-362">{room.details.bed}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-club-ink/80" data-id="element-363">
                    <Wifi size={16} className="text-club-gold" data-id="element-364" />
                    <span data-id="element-365">Free Wi-Fi</span>
                  </div>
                </div>

                <div className="mb-8" data-id="element-366">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-club-ink mb-3" data-id="element-367">
                    Key Amenities
                  </h4>
                  <ul className="space-y-2" data-id="element-368">
                    {room.amenities.map((amenity, i) => <li key={i} className="flex items-center gap-2 text-sm text-club-ink/70" data-id="element-369">
                        <div className="w-1.5 h-1.5 rounded-full bg-club-gold" data-id="element-370"></div>
                        {amenity}
                      </li>)}
                  </ul>
                </div>

                <div className="pt-6 border-t border-club-border flex gap-4" data-id="element-371">
                  <Link to="/contact" className="btn-primary" data-id="element-372">
                    Enquire Availability
                  </Link>
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>
    </div>;
}